import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { NotificationBell } from '../contexts/NotificationContext';
import { Chatbot } from './Chatbot';
import {
  Home,
  User,
  BookOpen,
  Calendar,
  MessageCircle,
  CreditCard,
  Star,
  LogOut,
  Menu,
  X,
  Shield,
  FileText,
  Rss,
  Bookmark
} from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
}

// Function to trigger chat notification (can be called from other components)
export const triggerChatNotification = () => {
  // This would typically be called when a new message arrives
  // For now, we'll use a custom event system
  window.dispatchEvent(new CustomEvent('newChatNotification'));
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hasNewChats, setHasNewChats] = useState(false);

  const { isAdmin } = useAuth();

  // Check for new chats (this would typically connect to your chat service)
  useEffect(() => {
    // TODO: Implement actual chat notification logic
    // For now, we'll simulate checking for new chats
    const checkForNewChats = () => {
      // This would typically make an API call to check for unread messages
      // setHasNewChats(hasUnreadMessages);
    };

    checkForNewChats();
    // Check every 30 seconds for new chats
    const interval = setInterval(checkForNewChats, 30000);

    return () => clearInterval(interval);
  }, []);

  // Clear chat notification when visiting chat page
  useEffect(() => {
    if (location.pathname === '/chat') {
      setHasNewChats(false);
    }
  }, [location.pathname]);

  // Listen for new chat notifications from other components
  useEffect(() => {
    const handleNewChatNotification = () => {
      setHasNewChats(true);
    };

    window.addEventListener('newChatNotification', handleNewChatNotification);
    return () => window.removeEventListener('newChatNotification', handleNewChatNotification);
  }, []);

  const navigation = [
    { name: 'Feed', href: '/feed', icon: Rss },
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Profile', href: '/profile', icon: User },
    { name: 'Skills', href: '/skills', icon: BookOpen },
    { name: 'Sessions', href: '/sessions', icon: Calendar },
    { name: 'Chat', href: '/chat', icon: MessageCircle },
    { name: 'My Notes', href: '/notes', icon: FileText },
    { name: 'Saved Feeds', href: '/saved-feeds', icon: Bookmark },
    { name: 'Payments', href: '/payments', icon: CreditCard },
    { name: 'Credit History', href: '/credit-history', icon: FileText },
    { name: 'Feedback', href: '/feedback', icon: Star },
    ...(isAdmin ? [{ name: 'Admin', href: '/admin', icon: Shield }] : []),
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 dark:bg-gray-900">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white dark:bg-gray-800 shadow-xl">
          <div className="flex h-16 items-center justify-between px-4 bg-gradient-to-r from-blue-600 to-teal-600">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110">
                <BookOpen className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">LearnLoop</h1>
                <p className="text-xs text-blue-100">Learning Platform</p>
              </div>
            </div>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-300 transform hover:scale-110"
            >
              <X className="h-5 w-5 text-white" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-2">
            {navigation.map((item, index) => {
              const isActive = location.pathname === item.href;
              const showNotification = item.name === 'Chat' && hasNewChats;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-lg transition-all duration-300 transform hover:scale-105 ${
                    isActive
                      ? 'bg-gradient-to-r from-blue-100 to-teal-100 dark:from-blue-900 dark:to-teal-900 text-blue-900 dark:text-blue-100 shadow-md border border-blue-200'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gradient-to-r hover:from-blue-50 hover:to-teal-50 dark:hover:from-gray-700 dark:hover:to-gray-600 hover:text-blue-600 dark:hover:text-gray-100 hover:shadow-sm'
                  }`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className={`p-1.5 rounded-md mr-2 transition-all duration-300 ${
                    isActive 
                      ? 'bg-gradient-to-r from-blue-500 to-teal-500 text-white shadow-md' 
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 group-hover:bg-blue-100 group-hover:text-blue-600'
                  }`}>
                    <item.icon className="h-4 w-4" />
                  </div>
                  <span className="font-medium text-xs">{item.name}</span>
                  {showNotification && (
                    <div className="ml-auto">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse shadow-md"></div>
                    </div>
                  )}
                </Link>
              );
            })}
          </nav>
          <div className="border-t border-blue-200 dark:border-gray-700 p-3 bg-gradient-to-r from-blue-50 to-teal-50 dark:from-gray-800 dark:to-gray-700">
            <div className="flex items-center p-2 rounded-xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-600 to-teal-600 flex items-center justify-center shadow-lg overflow-hidden">
                  {user?.profilePhotoUrl ? (
                    <img
                      src={user.profilePhotoUrl}
                      alt={`${user.fullName || user.username}'s profile`}
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        // Hide the broken image and show fallback
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  {/* Fallback initial - hidden if image loads successfully */}
                  <span className={`text-sm font-bold text-white ${user?.profilePhotoUrl ? 'hidden' : ''}`}>
                    {user?.username?.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{user?.fullName || user?.username}</p>
                <div className="flex items-center space-x-2 mt-1">
                  <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">{user?.credits} credits</p>
                </div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="mt-2 flex w-full items-center px-2 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-md"
            >
              <div className="p-1.5 rounded-md bg-red-100 dark:bg-red-900/30 mr-2">
                <LogOut className="h-4 w-4" />
              </div>
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
        <div className="flex flex-col flex-grow bg-white dark:bg-gray-800 border-r border-blue-200 dark:border-gray-700 shadow-xl">
          <div className="flex items-center justify-between h-16 px-4 bg-gradient-to-r from-blue-600 to-teal-600">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110">
                <BookOpen className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">LearnLoop</h1>
                <p className="text-xs text-blue-100">Learning Platform</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <NotificationBell />
            </div>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-2">
            {navigation.map((item, index) => {
              const isActive = location.pathname === item.href;
              const showNotification = item.name === 'Chat' && hasNewChats;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-lg transition-all duration-300 transform hover:scale-105 ${
                    isActive
                      ? 'bg-gradient-to-r from-blue-100 to-teal-100 dark:from-blue-900 dark:to-teal-900 text-blue-900 dark:text-blue-100 shadow-md border border-blue-200'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gradient-to-r hover:from-blue-50 hover:to-teal-50 dark:hover:from-gray-700 dark:hover:to-gray-600 hover:text-blue-600 dark:hover:text-gray-100 hover:shadow-sm'
                  }`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className={`p-1.5 rounded-md mr-2 transition-all duration-300 ${
                    isActive 
                      ? 'bg-gradient-to-r from-blue-500 to-teal-500 text-white shadow-md' 
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 group-hover:bg-blue-100 group-hover:text-blue-600'
                  }`}>
                    <item.icon className="h-4 w-4" />
                  </div>
                  <span className="font-medium text-xs">{item.name}</span>
                  {showNotification && (
                    <div className="ml-auto">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse shadow-md"></div>
                    </div>
                  )}
                </Link>
              );
            })}
          </nav>
          <div className="border-t border-blue-200 dark:border-gray-700 p-4 bg-gradient-to-r from-blue-50 to-teal-50 dark:from-gray-800 dark:to-gray-700">
            <div className="flex items-center p-3 rounded-xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 rounded-full bg-gradient-to-r from-blue-600 to-teal-600 flex items-center justify-center shadow-lg overflow-hidden">
                  {user?.profilePhotoUrl ? (
                    <img
                      src={user.profilePhotoUrl}
                      alt={`${user.fullName || user.username}'s profile`}
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        // Hide the broken image and show fallback
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  {/* Fallback initial - hidden if image loads successfully */}
                  <span className={`text-lg font-bold text-white ${user?.profilePhotoUrl ? 'hidden' : ''}`}>
                    {user?.username?.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{user?.fullName || user?.username}</p>
                <div className="flex items-center space-x-2 mt-1">
                  <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">{user?.credits} credits</p>
                </div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="mt-3 flex w-full items-center px-3 py-3 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-md"
            >
              <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30 mr-3">
                <LogOut className="h-4 w-4" />
              </div>
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col flex-1">
        {/* Mobile header */}
        <div className="lg:hidden flex items-center justify-between h-16 px-4 bg-gradient-to-r from-blue-600 to-teal-600 border-b border-blue-500">
          <div className="flex items-center space-x-2">
            <div className="h-6 w-6 bg-white/20 rounded-lg flex items-center justify-center">
              <BookOpen className="h-4 w-4 text-white" />
            </div>
            <h1 className="text-lg font-semibold text-white">LearnLoop</h1>
          </div>
          <div className="flex items-center space-x-2">
            <NotificationBell />
            <button 
              onClick={() => triggerChatNotification()}
              className="p-2 text-sm bg-red-500 text-white rounded-md hover:bg-red-600"
              title="Test Chat Notification"
            >
              Test Chat
            </button>
            <button onClick={() => setSidebarOpen(true)}>
              <Menu className="h-6 w-6 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-gradient-to-br from-blue-50 via-white to-teal-50 dark:bg-gray-900">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>
      
      {/* Chatbot - Available on all pages */}
      <Chatbot />
    </div>
  );
};

export default Layout; 